'use client';

import { faRotate } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import InputField from '@pickleballinc/react-ui/InputField';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import ToggleButtonForm from '@/components/atoms/ToggleButtonForm';
import { ActionButtons, DatetimePickerForm } from '@/components/molecules';
import { ModalWrapper } from '@/components/organisms';
import { EditTeamSettingsData, editTeamSettingsSchema } from '@/entities';

interface EditTeamSettingsFormProps {
	title: string;
	onClose: () => void;
	onSubmit: (data: EditTeamSettingsData) => void;
	currentCode?: string;
	allowFreeAgentRequests?: boolean;
	isActiveCode?: boolean;
	numberOfUses?: string | number | null;
	dateOfExpiry?: string | null;
	dateDisabled?: string | null;
	codeUuid?: string;
	teamUuid?: string;
	teamName: string;
}

const generateRandomCode = (): string => {
	const characterSet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	const length = 6;

	return Array.from({ length }, () => {
		const randomIndex = Math.floor(Math.random() * characterSet.length);
		return characterSet[randomIndex];
	}).join('');
};

export const EditTeamSettingsForm = ({
	title,
	onClose,
	onSubmit,
	currentCode,
	dateOfExpiry,
	numberOfUses,
	allowFreeAgentRequests,
	isActiveCode,
	teamName
}: EditTeamSettingsFormProps) => {
	const [isFreeAgent, setIsFreeAgent] = useState(allowFreeAgentRequests);
	const [isActive, setIsActive] = useState(isActiveCode);

	const form = useForm<EditTeamSettingsData>({
		resolver: zodResolver(editTeamSettingsSchema)
	});

	const { formState, register, handleSubmit } = form;

	return (
		<ModalWrapper title={title} onClose={onClose} className="max-w-[524px] gap-0 p-6">
			<FormProvider {...form}>
				<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col" autoComplete="off">
					<div className="mb-10 flex flex-col">
						<div>
							<span className="mt-4 block text-lg font-semibold leading-7 text-gray-500">{teamName}</span>
							<hr className="my-4 h-px shrink-0 self-stretch rounded-md bg-brand-300" />
						</div>

						<div>
							<h2 className=" text-2xl font-semibold leading-8 text-gray-900">Invitation codes</h2>
							<div className="my-4 flex justify-between">
								<p className={`text-base font-bold leading-6 text-gray-900 ${!isActive ? 'opacity-25' : ''}`}>
									Current code:
									<span className={`ml-1 text-base font-bold leading-6 text-[#36F]`}>{currentCode}</span>
								</p>

								<div className="flex cursor-pointer items-center justify-center gap-2 w-[120px]">
									<div onClick={() => setIsActive((prev) => !prev)} className="flex items-center">
										<ToggleButtonForm name="isActive" defaultValue={isActiveCode} />
									</div>
									<span className="text-base font-medium leading-6 text-gray-900 block w-full">
										{isActive ? 'Active' : 'Inactive'}
									</span>
								</div>
							</div>

							<div className="mb-4 w-64">
								<span className="block pb-1.5 text-sm font-medium leading-5 text-gray-700">Expiration Date</span>
								<DatetimePickerForm
									defaultValue={dateOfExpiry}
									name="expirationDate"
									placeholder="Select expiration date"
									granularity="time"
									yearRange={25}
								/>
							</div>

							<div>
								<span className="block pb-1.5 text-sm font-medium leading-5 text-gray-700">Number of times code can be used</span>
								<InputField
									focusColor="orange"
									{...register('numberOfTimesCodeCanBeUsed')}
									defaultValue={numberOfUses || ''}
									hasError={!!formState.errors.numberOfTimesCodeCanBeUsed}
									hint={formState.errors.numberOfTimesCodeCanBeUsed?.message?.toString()}
									type="number"
									min={0}
									className="w-[260px] rounded-lg border border-gray-300 bg-white shadow-xs "
									inputProps={{
										className: 'px-3 py-2 bg-white text-gray-900 rounded-lg h-10'
									}}
								/>
								<span className="block pt-1.5 text-sm font-medium leading-5 text-gray-600">Leave blank for unlimited</span>
							</div>

							<div className="mt-6">
								<span className="block pb-1.5 text-sm font-medium leading-5 text-gray-700">Enter new invitation code</span>
								<div className="flex w-full items-start gap-4">
									<InputField
										focusColor="orange"
										{...register('invitationCode')}
										defaultValue={currentCode}
										maxLength={6}
										minLength={6}
										hasError={!!formState.errors.invitationCode}
										hint={formState.errors.invitationCode?.message?.toString()}
										className="max-w-[320px] sm:max-w-none rounded-lg border border-gray-300 bg-white shadow-xs sm:w-[320px]"
										inputProps={{
											className: 'px-3 py-2 bg-white text-gray-900 rounded-lg h-10'
										}}
									/>

									<div
										onClick={() => {
											const generatedInvitationCode = generateRandomCode();
											form.setValue('invitationCode', generatedInvitationCode, { shouldDirty: true });
										}}
										className="flex h-10 cursor-pointer items-center justify-center gap-2"
									>
										<FontAwesomeIcon icon={faRotate} className="text-xl font-normal leading-5 text-gray-700" />
										<span className="text-xs font-normal leading-[18px] text-gray-700">click to generate</span>
									</div>
								</div>
								<span className="block pt-1.5 text-sm font-medium leading-5 text-gray-600">
									Codes must be unique & use 6 characters
								</span>
							</div>
						</div>
						<div>
							<hr className=" mb-4 mt-6 h-px shrink-0 self-stretch rounded-md bg-brand-300" />

							<div className="my-0 flex justify-between items-center gap-10">
								<p className="text-2xl font-semibold leading-8 text-gray-900 text-nowrap">Free agents</p>
								<div className="flex cursor-pointer items-center justify-end gap-2 w-[125px] sm:w-[195px]">
									<div onClick={() => setIsFreeAgent((prev) => !prev)} className="flex items-center">
										<ToggleButtonForm name="isFreeAgent" defaultValue={allowFreeAgentRequests} />
									</div>
									<span className="text-base font-medium leading-6 text-gray-900 block w-full">
										{isFreeAgent ? 'Requests active' : 'Requests inactive'}
									</span>
								</div>
							</div>
						</div>
					</div>
					<ActionButtons
						onClose={onClose}
						submitButtonText={'Save'}
						closeButtonText="Cancel"
						submitButtonClassName={'bg-orange-500 hover:bg-orange-700 text-white disabled:!bg-darkorange-300'}
						submitButtonDisabled={formState.isSubmitting}
					/>
				</form>
			</FormProvider>
		</ModalWrapper>
	);
};
